import { usePilotage } from '../context';
import { DataTable, Meter, OkMark, Panel, Row, SectionState, Unavailable } from '../ui';
import { DailyChart, Legend, TimeChart } from '../charts';
import { bytes, dec, duration, num, pct } from '../format';
import { loadColor } from '../theme';
import { cronDetail, databasesDetail, infraDetail } from '../details';

/** Écran 4 — Infrastructure : nœuds, charges, bases, files, tâches planifiées */
export function InfraScreen() {
  const { payload, palette: p, openDetail } = usePilotage();
  const s = payload.sections;
  const infra = s.infra?.data;
  const db = s.databases?.data;
  const q = s.queues?.data;

  if (!infra) return <Unavailable section={s.infra} what="Infrastructure" />;

  const crons = infra.cronjobs;
  const badCrons = crons.filter((c) => c.stale || c.failedLast24h > 0);

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12 xl:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
      {/* Nœuds */}
      <Panel title="Nœuds AKS" className="xl:col-span-12" right={<SectionState section={s.infra} />} onOpen={() => openDetail(infraDetail(payload, p))}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          {infra.nodes.map((n) => (
            <div key={n.name} className="space-y-1.5 rounded-lg border border-slate-200 p-2 dark:border-slate-800">
              <div className="flex items-center justify-between gap-1">
                <span className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200" title={n.name}>
                  {n.pool} · {n.name.split('-').pop()}
                </span>
                <OkMark ok={n.ready} label={n.ready ? '' : 'KO'} />
              </div>
              <Meter label="CPU" value={n.cpuPct} color={loadColor(n.cpuPct)} />
              <Meter label={`Mém. ${dec(n.memGb, 0)} Go`} value={n.memPct} color={loadColor(n.memPct, 80, 90)} />
              <Meter label="Disque" value={n.diskPct} color={loadColor(n.diskPct, 80, 90)} />
              <div className="text-[11px] text-slate-400">
                {num(n.pods)} / {num(n.podCapacity)} pods · {num(n.cores)} cœurs
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Courbes */}
      <Panel
        title="Pool applicatif (24 h)"
        className="xl:col-span-5"
        right={
          <Legend
            defs={[
              { metric: 'infra.app_cpu_pct', label: 'CPU', color: p.v2 },
              { metric: 'infra.app_mem_pct', label: 'Mémoire', color: p.third },
              { metric: 'infra.cpu_pct_max', label: 'CPU nœud le plus chargé', color: p.v1 },
            ]}
          />
        }
      >
        <TimeChart
          defs={[
            { metric: 'infra.app_cpu_pct', label: 'CPU', color: p.v2 },
            { metric: 'infra.app_mem_pct', label: 'Mémoire', color: p.third },
            { metric: 'infra.cpu_pct_max', label: 'CPU nœud le plus chargé', color: p.v1 },
          ]}
          format={(n) => `${dec(n, 0)} %`}
          domainMax={100}
          height="100%"
        />
      </Panel>
      <Panel title="Charges de travail" subtitle="CPU / mémoire actuels" className="xl:col-span-4" bodyClassName="overflow-y-auto" onOpen={() => openDetail(infraDetail(payload, p))}>
        <DataTable
          rows={[...infra.workloads].sort((a, b) => b.cpuCores - a.cpuCores)}
          columns={[
            { key: 'l', label: 'Charge', render: (r) => r.label },
            { key: 'p', label: 'Pods', align: 'right', render: (r) => num(r.pods) },
            { key: 'c', label: 'CPU', align: 'right', render: (r) => dec(r.cpuCores, 2) },
            { key: 'm', label: 'Mém.', align: 'right', render: (r) => bytes(r.memBytes) },
            { key: 'r', label: 'Redém.', align: 'right', render: (r) => num(r.restarts24h) },
          ]}
        />
      </Panel>
      <Panel title="Bases et files" className="xl:col-span-3" right={<SectionState section={s.databases} />} onOpen={() => openDetail(databasesDetail(payload, p))}>
        {db ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase text-slate-400">MariaDB (V1 + ADM)</div>
            <Row label="Connexions" value={`${num(db.mariadb.threadsConnected)} / ${num(db.mariadb.maxConnections)}`} />
            <Row label="Requêtes / s" value={dec(db.mariadb.qps)} />
            <Row label="Volume" value={`${dec(db.mariadb.sizeGb, 0)} Go`} />
            <div className="pt-1 text-xs font-medium uppercase text-slate-400">PostgreSQL (V2)</div>
            <Row label="Connexions" value={`${num(db.postgres.connections?.total)} / ${num(db.postgres.maxConnections)}`} />
            <Row label="Transactions / s" value={dec(db.postgres.tps)} />
            <Row label="Requête la plus longue" value={duration(db.postgres.longestQuerySec)} />
            <Row label="Volume" value={`${dec(db.postgres.sizeGb, 0)} Go`} />
            {q && (
              <>
                <div className="pt-1 text-xs font-medium uppercase text-slate-400">Files V2</div>
                <Row label="En attente (files actives)" value={num(q.backlog)} />
                <Row label="En échec (failed)" value={num(q.failed)} />
                <Row label="Sans worker" value={num(q.unconsumed)} />
              </>
            )}
          </div>
        ) : (
          <Unavailable section={s.databases} what="Bases" />
        )}
      </Panel>

      {/* Historique et crons */}
      <Panel
        title="CPU par jour"
        subtitle="moyenne et pic du pool applicatif"
        className="xl:col-span-5"
        right={
          <Legend
            defs={[
              { metric: 'infra.app_cpu_pct_avg_day', label: 'Moyenne', color: p.v2 },
              { metric: 'infra.app_cpu_pct_max_day', label: 'Pic', color: p.v1 },
            ]}
          />
        }
      >
        <DailyChart
          defs={[
            { metric: 'infra.app_cpu_pct_avg_day', label: 'Moyenne', color: p.v2 },
            { metric: 'infra.app_cpu_pct_max_day', label: 'Pic', color: p.v1 },
          ]}
          format={(n) => `${dec(n, 0)} %`}
          domainMax={100}
          height="100%"
        />
      </Panel>
      <Panel title="Tâches planifiées" subtitle={`${num(crons.length)} CronJobs · ${num(badCrons.length)} à surveiller`} className="xl:col-span-4" bodyClassName="overflow-y-auto" onOpen={() => openDetail(cronDetail(payload))}>
        <div className="space-y-0.5">
          {(badCrons.length ? badCrons : crons.slice(0, 10)).map((c) => (
            <Row
              key={c.name}
              label={<span title={c.name}>{c.short}</span>}
              value={<OkMark ok={!c.stale && !c.failedLast24h} label={c.stale ? 'en retard' : c.failedLast24h ? `${c.failedLast24h} échec(s)` : 'OK'} />}
              hint={c.lastSuccessAgoSec !== null ? duration(c.lastSuccessAgoSec) : 'jamais'}
            />
          ))}
        </div>
      </Panel>
      <Panel title="Problèmes Kubernetes" className="xl:col-span-3" bodyClassName="overflow-y-auto">
        {infra.problems.pods.length + infra.problems.unavailable.length + infra.problems.restarts1h.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <OkMark ok label="Aucun pod en erreur, aucune réplique manquante" />
          </div>
        ) : (
          <div className="space-y-1">
            {infra.problems.pods.map((x) => (
              <Row key={x.pod} label={<span title={x.pod}>{x.pod}</span>} value={x.reason} />
            ))}
            {infra.problems.unavailable.map((x) => (
              <Row key={x.name} label={x.name} value={`${x.missing} manquante(s)`} />
            ))}
            {infra.problems.restarts1h.map((x) => (
              <Row key={x.pod + x.container} label={x.pod} value={`${x.count}× / 1 h`} />
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-slate-400">
          {num(infra.cluster.podsRunning)} pods en cours · pool applicatif {pct(infra.cluster.appPodCapacity ? (100 * infra.cluster.appPods) / infra.cluster.appPodCapacity : null, 0)} de la capacité
        </p>
      </Panel>
    </div>
  );
}
